/**
 * useGridSettings — Shared grid density + column visibility settings hook
 *
 * Provides density toggle (compact / default / comfortable) and
 * column visibility controls with localStorage persistence.
 * Extracted from TmsPlannerPage pattern for reuse across FMS/TMS/WMS.
 */

import { useState, useCallback, useMemo } from 'react';

// ── Types ──────────────────────────────────────────

export type RowDensity = 'compact' | 'default' | 'comfortable';

export interface DensityOption {
  value: RowDensity;
  label: string;
  icon: string;
}

export interface DensityClasses {
  cell: string;
  text: string;
  row: string;
}

export interface GridSettings {
  density: RowDensity;
  setDensity: (d: RowDensity) => void;
  cycleDensity: () => void;
  densityClasses: DensityClasses;
  densityOptions: DensityOption[];
}

// ── Constants ──────────────────────────────────────

export const DENSITY_OPTIONS: DensityOption[] = [
  { value: 'compact', label: 'Compact', icon: '▤' },
  { value: 'default', label: 'Default', icon: '▥' },
  { value: 'comfortable', label: 'Comfortable', icon: '☰' },
];

export const DENSITY_CLASSES: Record<RowDensity, DensityClasses> = {
  compact: {
    cell: 'px-1 py-0.5',
    text: 'text-[10px] leading-tight',
    row: 'h-6',
  },
  default: {
    cell: 'px-1.5 py-1',
    text: 'text-xs leading-snug',
    row: 'h-8',
  },
  comfortable: {
    cell: 'px-2 py-1.5',
    text: 'text-xs leading-normal',
    row: 'h-10',
  },
};

// ── Hook ───────────────────────────────────────────

export function useGridSettings(storageKey: string): GridSettings {
  const [density, setDensityState] = useState<RowDensity>(() => {
    try {
      const stored = localStorage.getItem(`${storageKey}-density`);
      if (stored && (stored === 'compact' || stored === 'default' || stored === 'comfortable')) {
        return stored as RowDensity;
      }
    } catch { /* ignore */ }
    return 'default';
  });

  const setDensity = useCallback((d: RowDensity) => {
    setDensityState(d);
    try { localStorage.setItem(`${storageKey}-density`, d); } catch { /* ignore */ }
  }, [storageKey]);

  const cycleDensity = useCallback(() => {
    setDensityState((prev) => {
      const idx = DENSITY_OPTIONS.findIndex((o) => o.value === prev);
      const next = DENSITY_OPTIONS[(idx + 1) % DENSITY_OPTIONS.length]!.value;
      try { localStorage.setItem(`${storageKey}-density`, next); } catch { /* ignore */ }
      return next;
    });
  }, [storageKey]);

  const densityClasses = useMemo(() => DENSITY_CLASSES[density], [density]);

  return {
    density,
    setDensity,
    cycleDensity,
    densityClasses,
    densityOptions: DENSITY_OPTIONS,
  };
}
